<script setup name="TvbVideoDetailPlayer">
import {
  inject,
  onMounted,
  onServerPrefetch,
  computed,
  ref,
  watch,
  nextTick,
  onUnmounted
} from 'vue'
import { getVideoExtra } from '@/api/video'
import { getCategoryIdList, getRecommendedVideos } from '@/api/article.js'
import { getAds } from '@/api/app'
import { pageview } from '@/utils/gtm'
import { buildInstreamAdTagUrl, getInstreamAdSize, appendUtmParams } from '@/utils/index'
import TvbPlayer from '@/components/TvbPlayer/index.vue'
import dayjs from 'dayjs'
import { useRoute, useRouter } from 'vue-router'

const size = inject('size')
const currentVideo = inject('currentVideo')
const video = ref(currentVideo.value || {})
const setCurrentVideo = inject('setCurrentVideo')
const nextVideo = inject('nextVideo')
// 注入顶层 extraLoaded 标志：getExtra 完成后置 true，用于 gate 非PR/MR广告位渲染
const extraLoaded = inject('extraLoaded')
// 注入上一集视频，用于播放器上一集按钮切换功能
const prevVideo = inject('prevVideo')
const tvbPlayerRef = ref(null)
const isPreview = inject('isPreview')
const route = useRoute()
import { useHead } from '@unhead/vue'

// 推荐视频
const recommendedVideos = ref([])
const isLoading = ref(false)
const error = ref(null)

const fetchRecommendedVideos = async () => {
  if (!currentVideo.value?.id) return
  isLoading.value = true
  error.value = null
  try {
    const { code, data } = await getRecommendedVideos({ exclude_ids: currentVideo.value.id })
    if (code === 0) {
      recommendedVideos.value = data.list || []
    } else {
      throw new Error('Failed to fetch recommended videos')
    }
  } catch (err) {
    error.value = err.message
  } finally {
    isLoading.value = false
  }
}

const VITE_HOST_URL = import.meta.env.VITE_HOST_URL

const ldJsonScript = computed(() => {
  if (!currentVideo.value) return ''
  const videoData = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video?.value?.seo_title || '',
    description: video?.value?.meta_description || '',
    datePublished: dayjs?.unix(video?.value?.publish_at || 0)?.toISOString() || '',
    dateModified: dayjs?.unix(video?.value?.updated_at || 0)?.toISOString() || '',
    thumbnailUrl: [currentVideo.value.og_image_info?.large],
    uploadDate: dayjs?.unix(video?.value?.publish_at || 0)?.toISOString() || '',
    contentUrl: video?.value?.video_url || '',
    embedUrl: canonical.value
  }
  if (video?.value?.video_time > 0) {
    videoData.duration = video?.value?.video_time
      ? dayjs('2000-01-01 00:00:00')
          .second(video?.value?.video_time || 0)
          .format('[PT]m[M]s[S]')
      : 'PT1S'
  }
  const BreadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '主頁',
        item: VITE_HOST_URL
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: video.value?.category_info?.parent?.title,
        item: video.value?.category_info?.parent?.link
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: video.value?.category_info?.title,
        item: video.value?.category_info?.link
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: video.value?.title,
        item: canonical.value
      }
    ]
  }
  return [videoData, BreadcrumbList]
})

const canonical = computed(() => VITE_HOST_URL + (video?.value?.uri?.slice(1) || ''))

const scriptData = computed(() => ({
  link: [
    {
      rel: 'canonical',
      href: canonical.value
    },
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: canonical.value
    },
    {
      rel: 'alternate',
      hreflang: 'zh-hk',
      href: canonical.value
    }
  ],
  script: [
    {
      id: 'video-ld-json',
      innerHTML: JSON.stringify(ldJsonScript.value),
      type: 'application/ld+json'
    }
  ]
}))
const { patch } = useHead(scriptData)

const getExtra = async (isNotPlay = false) => {
  if (!currentVideo.value?.id) return
  const extraInfoParams = { video_id: currentVideo.value.id }
  if (isPreview.value) {
    extraInfoParams['key'] = route?.params?.videoId
  }
  const { code, data } = await getVideoExtra(extraInfoParams)
  if (code === 0) {
    isError.value = data.region_only === 1
    try {
      const categoryInfo = await getCategoryIdList({ category_id: currentVideo.value.category_id })
      if (categoryInfo?.code === 0) {
        data.category_info = categoryInfo?.data || {}
      }
    } catch (error) {
      console.error(error)
    }
    video.value = { ...currentVideo.value, ...data }
    setCurrentVideo(video.value)
    patch(scriptData.value)
    if (!isPreview.value) {
      // TC-1631: 保留 utm 参数在地址栏
      window.history.replaceState(history.state, null, appendUtmParams(currentVideo.value.uri))
    }
    // 视频数据加载完成后，根据 is_show_ad 获取 Instream 广告配置
    fetchInstreamAds()
    // 自动播放逻辑 - 延迟确保播放器已完全初始化
    if (!isNotPlay) {
      setTimeout(() => {
        if (tvbPlayerRef.value) {
          tvbPlayerRef.value.play().catch((err) => {
            console.warn('Auto-play failed:', err)
            // 自动播放失败时不阻止用户手动播放
          })
        }
      }, 500)
    }
    pageview({
      page_title: 'video_detail_page',
      screen_class: currentVideo.value?.seo_title || '',
      screenID: 'S007',
      eventCategory: currentVideo.value?.category_name || '',
      eventLabel: 'video_detail',
      eventID: 'S007',
      screenLabel: location.href
    })
  } else {
    isError.value = true
  }

  // 标记扩展信息已加载完成（无论成功或失败），允许 topbn/to/lrec/sb/bn 等广告位渲染
  // 失败时 category_info.path 仍为 undefined，sub_category_path 会兜底为 'x'，与修复前一致
  if (extraLoaded) extraLoaded.value = true

  return data
}

const videoSrc = computed(() => currentVideo.value?.video_url)
const videoType = computed(() => ({ 1: 'youtube', 2: 'shaka' })[currentVideo.value?.video_type])
const posterUrl = computed(() =>
  ['s', 'xs'].includes(size)
    ? currentVideo.value?.image_info?.small
    : currentVideo.value?.image_info?.large
)
const isError = ref(false)

const videoClarityConfig = computed(() => currentVideo.value?.video_clarity)

const subtitleConfig = computed(() => currentVideo.value?.subtitle)

// ========== Instream 广告逻辑 ==========
const adTagUrl = ref('')
// 当前播放的广告类型，用于区分 pr/mr 的 onAdEnd 处理
const currentAdType = ref('pr')

// 是否显示 Instream 广告：is_show_ad === 2 时不显示，其他情况默认显示
const isShowAd = computed(() => currentVideo.value?.is_show_ad ?? 1)
const showInstreamAds = computed(() => isShowAd.value !== 2)

/**
 * 构造 VAST/VMAP 广告标签 URL
 * 使用 Google Ad Manager 的 GPT 标签格式
 * @param {Object} adsData - 广告接口返回的配置数据
 * @param {string} adType - 广告类型 'pr' 或 'mr'，默认 'pr'
 * @returns {string} 完整的 VAST adTagUrl
 */
const constructAdTagUrl = (adsData, adType = 'pr') => {
  const adUnitPrefix = adsData.ad_unit_prefix
  if (!adUnitPrefix) return ''

  // 从接口返回的 size_setting 中根据屏幕尺寸匹配广告尺寸
  const adSize = getInstreamAdSize(adsData.ads_list, adType)

  return buildInstreamAdTagUrl({
    adUnitPrefix,
    adChildUnit: adType,
    adSize,
    customParams: {
      adtype: 'vod',
      vtype: 'vod',
      content_type: 'video',
      sub_category_path: currentVideo.value?.category_info?.path || 'x',
      // video_id 统一使用 /api/video/xxx 返回的真实 id（修复 TC-1607）
      video_id: currentVideo.value?.id || 'x'
    },
    // 视频时长（秒），GAM 依据此参数返回 mid-roll 广告（TC-1596）
    videoDuration: currentVideo.value?.video_time || 0
  })
}

/** 获取 pr Instream 广告配置并构造 adTagUrl */
const fetchInstreamAds = async () => {
  if (!showInstreamAds.value) return
  try {
    const { data } = await getAds({ position: 14, adtype: 'pr' })
    if (data?.ad_unit_prefix) {
      currentAdType.value = 'pr' // 重置广告类型，确保切换视频后从 pr 开始
      adTagUrl.value = constructAdTagUrl(data, 'pr')
    }
  } catch (error) {
    console.error('Failed to fetch instream ads:', error)
  }
}

/** pr 广告结束后请求 mr 广告（SDK 自动在 mid-roll 时间点播放） */
const fetchMrAds = async () => {
  if (!showInstreamAds.value) return
  try {
    const { data } = await getAds({ position: 14, adtype: 'mr' })
    if (data?.ad_unit_prefix) {
      const mrAdTagUrl = constructAdTagUrl(data, 'mr')
      if (mrAdTagUrl) {
        // SDK 收到 mr 的 VMAP 后自动跟踪内容进度，在 mid-roll 时间点播放
        // 不需要手动 pause()，SDK 自动管理内容暂停/恢复
        tvbPlayerRef.value?.requestAds(mrAdTagUrl)
      }
    }
  } catch (error) {
    console.error('Failed to fetch mr ads:', error)
  }
}

// 监听 showInstreamAds 变化，动态获取广告配置
watch(showInstreamAds, (show) => {
  if (show) {
    fetchInstreamAds()
  } else {
    adTagUrl.value = ''
  }
})

// Instream 广告事件处理
const onAdStart = () => {
  // 广告开始播放
}

const onAdEnd = () => {
  // pr 广告结束后请求 mr；mr 结束后不做任何事，视频自动恢复播放
  if (currentAdType.value === 'pr') {
    currentAdType.value = 'mr'
    fetchMrAds()
  }
}

const onAdError = (error) => {
  console.error('Instream ad error:', error)
}

const onContentResume = () => {
  // 广告结束，内容恢复播放
}

// 格式化推荐视频列表以适配 TvbPlayer 组件
const featuredVideos = computed(() => {
  return recommendedVideos.value.map((item) => ({
    cover: item.image_info.small,
    title: item.title,
    url: item.uri
  }))
})

onMounted(() => {
  getExtra()
  fetchRecommendedVideos()
})
onServerPrefetch(getExtra)

const stateChangeTimeout = ref(null)

// 处理播放器状态变化，包括播放结束自动切换下一集
const onStateChange = ({ state }) => {
  // 防抖处理
  if (stateChangeTimeout.value) {
    clearTimeout(stateChangeTimeout.value)
  }

  stateChangeTimeout.value = setTimeout(() => {
    if (state === 'ended' && nextVideo.value?.id) {
      // 只有在没有推荐视频时才立即切换
      // 有推荐视频时，由推荐视频组件的倒计时控制切换
      const hasFeaturedVideos = featuredVideos.value.length > 0
      if (!hasFeaturedVideos) {
        setCurrentVideo(nextVideo.value)
      }
    }
    isError.value = state === 'error'
  }, 100)
}

// 处理上一集按钮点击 - 切换到上一集视频
const onPrev = () => {
  if (prevVideo.value?.id) {
    setCurrentVideo(prevVideo.value)
  }
}

// 处理下一集按钮点击 - 切换到下一集视频
const onNext = () => {
  if (nextVideo.value?.id) {
    setCurrentVideo(nextVideo.value)
  }
}

const router = useRouter()

// 处理推荐视频导航 - 点击推荐视频跳转到对应页面
const onRecommendNavigate = (video) => {
  if (video?.url) {
    // 使用路由导航到推荐视频页面
    router.push(video.url)
  }
}

onUnmounted(() => {
  if (stateChangeTimeout.value) {
    clearTimeout(stateChangeTimeout.value)
  }
})

defineExpose({ getExtra: getExtra })
</script>

<template>
  <div class="tvb-video-detail_player-wrap">
    <div class="tvb-video-detail_player" :class="[size]">
      <tvb-player
        :youbora-options="{
          videoTitle: currentVideo.title,
          programTitle: currentVideo.category_name,
          videoSrc,
          contentId: currentVideo?.id
        }"
        :content-id="currentVideo?.content_id"
        v-if="videoSrc || currentVideo?.video_id"
        :src="videoSrc"
        :poster-url="posterUrl"
        :region-only="currentVideo?.region_only"
        :type="videoType"
        style="height: 100%; width: 100%"
        :mute="false"
        @stateChange="onStateChange"
        @prev="onPrev"
        @next="onNext"
        @recommend-navigate="onRecommendNavigate"
        ref="tvbPlayerRef"
        drag-box
        :drag-box-disabled="['s', 'xs'].includes(size)"
        :title="currentVideo?.title || currentVideo?.seo_title || ''"
        :show-episode-navigation="true"
        :video-clarity="videoClarityConfig"
        :subtitle="subtitleConfig"
        :featured-videos="featuredVideos"
        :ad-tag-url="adTagUrl"
        :show-instream-ads="showInstreamAds"
        @ad-start="onAdStart"
        @ad-end="onAdEnd"
        @ad-error="onAdError"
        @content-resume="onContentResume"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tvb-video-detail_player-wrap {
  // 定义播放器最大宽度，供 wrapper 和 player 共用
  // 垂直预算构成（TC-1481 顶部间距调整后核对）：
  // - 36px = 播放器上方固定间距：.tvb-main padding-top(8px) + 面包屑行高(20px) + .tvb-video-detail margin-top(8px)
  //   页面顶部间距由 16px 改为 8px 后，由 44px 同步修正为 36px，播放器底边位置保持不变（仍在视口内完整显示）
  // - 122px = 底部预留值（历史遗留，构成无法完全考证，含 .tvb-main 底部内边距等，按工单要求保持不变）
  --player-max-width: calc((100vh - 122px - 36px - var(--header-height)) * 16 / 9);

  // 填充 flex 容器可用空间，确保首次布局宽度正确（解决抖动）
  flex-grow: 1;
  // 限制 wrapper 最大宽度，使 justify-content: center 有剩余空间可居中
  max-width: var(--player-max-width);
}

.tvb-video-detail_player {
  min-width: 640px;
  min-height: 360px;
  // 引用 wrapper 上定义的自定义属性，避免重复计算
  max-width: var(--player-max-width);

  max-height: fit-content;
  height: auto;
  width: 100%;
  // width: auto;
  // height: auto;
  aspect-ratio: 16/9;
  border-radius: 24px;
  background: #000;
  overflow: hidden;
  z-index: 9;

  // Safari浏览器特殊处理：确保高度计算正确
  @supports (-webkit-touch-callout: none) or (background: -webkit-named-image(i)) {
    height: 100%;
    // 与 --player-max-width 的垂直预算保持一致（顶部间距修正后 44px → 36px）
    max-height: calc((100vh - 122px - 36px - var(--header-height)));
  }

  @media (max-width: 1143px) {
    height: auto;
    width: 100%;
    border-radius: 0;
    overflow: visible;
    background: transparent;
    // TC-1482：移动端（≤798px）播放器外层容器白色底色，作为视频加载前的占位底色
    // 播放画面仍由下方 :deep(.tvb-player) 的 #000 黑底保证，799-1143px 桌面形态不受影响
    @media (max-width: 798px) {
      background: #fff;
    }
    aspect-ratio: unset;
    height: fit-content;

    // Safari浏览器特殊处理：移动端保持宽高比
    @supports (-webkit-touch-callout: none) or (background: -webkit-named-image(i)) {
      aspect-ratio: 16/9;
      height: auto;
    }

    :deep(.tvb-player) {
      height: auto;
      aspect-ratio: 16/9;
      width: 100vw !important;
      background: #000;
      transform: translateX(calc(0px - var(--tvb-main-left)));
    }
  }

  &.m,
  &.s,
  &.xs {
    min-width: unset;
    min-height: unset;
  }
}
</style>
